import { Link } from "react-router-dom";
import { BookOpen, FlaskConical, Microscope, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const STUDIES = [
    {
        id: "ST-001",
        title: "BPC-157: Pentadecapeptide Mechanisms in Tendon Remodeling",
        category: "Healing",
        date: "2025-11",
        excerpt: "A meta-analysis of 14 in-vivo studies showing accelerated collagen deposition and angiogenic response across rodent Achilles models.",
    },
    {
        id: "ST-002",
        title: "Dual Incretin Agonism: GIP/GLP-1 Synergistic Signaling Pathways",
        category: "Metabolic",
        date: "2025-09",
        excerpt: "Comparative analysis of tirzepatide vs. semaglutide in DIO mouse models — receptor binding kinetics and downstream cAMP response.",
    },
    {
        id: "ST-003",
        title: "Mitochondrial-Targeted Tetrapeptides and Cardiolipin Binding",
        category: "Cellular",
        date: "2025-07",
        excerpt: "SS-31 binding affinity for cardiolipin under oxidative stress conditions — implications for bioenergetics research.",
    },
    {
        id: "ST-004",
        title: "Copper Tripeptide GHK-Cu: Genomic Modulation in Fibroblast Cultures",
        category: "Cellular",
        date: "2025-05",
        excerpt: "RNA-seq analysis demonstrates differential expression of 4,192 genes in human dermal fibroblasts exposed to nanomolar GHK-Cu.",
    },
];

export default function Research() {
    const { t } = useTranslation();
    return (
        <div data-testid="research-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
            <div className="border-b border-neutral-200 pb-12 mb-12">
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("research.eyebrow")}</div>
                <h1 className="font-display text-5xl lg:text-7xl font-extrabold uppercase tracking-tighter">
                    {t("research.titleLine1")}
                    <br />
                    <span className="text-[#0077b6]">{t("research.titleAccent")}</span>
                </h1>
                <p className="text-neutral-600 max-w-2xl mt-6 leading-relaxed">
                    {t("research.intro")}
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 mb-16">
                {[
                    { icon: BookOpen, n: "1,247", l: t("research.statsStudies") },
                    { icon: FlaskConical, n: "42", l: t("research.statsCompounds") },
                    { icon: Microscope, n: "18", l: t("research.statsPartners") },
                ].map((s, i) => (
                    <div key={s.l} className="bg-white p-8" data-testid={`research-stat-${i}`}>
                        <s.icon className="w-5 h-5 text-[#0077b6] mb-4" />
                        <div className="font-display text-4xl font-extrabold text-neutral-900">{s.n}</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 mt-1">{s.l}</div>
                    </div>
                ))}
            </div>

            <div className="space-y-px bg-white/5 rounded-xl overflow-hidden">
                {STUDIES.map((s) => (
                    <article
                        key={s.id}
                        className="bg-white hover:bg-[#f8f8f8] p-6 sm:p-8 lg:p-10 group transition-colors cursor-pointer"
                        data-testid={`study-${s.id}`}
                    >
                        <div className="flex items-start justify-between gap-4 sm:gap-6">
                            <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] mb-3">
                                    <span className="text-[#0077b6]">{s.id}</span>
                                    <span className="text-neutral-500 hidden sm:inline">·</span>
                                    <span className="text-neutral-500">{s.category}</span>
                                    <span className="text-neutral-500 hidden sm:inline">·</span>
                                    <span className="text-neutral-500 font-mono">{s.date}</span>
                                </div>
                                <h3 className="font-display text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight group-hover:text-black transition-colors">
                                    {s.title}
                                </h3>
                                <p className="text-neutral-600 mt-3 max-w-3xl leading-relaxed text-sm sm:text-base">{s.excerpt}</p>
                            </div>
                            <ArrowUpRight className="w-5 sm:w-6 h-5 sm:h-6 text-neutral-500 group-hover:text-black group-hover:rotate-45 transition-all flex-shrink-0" />
                        </div>
                    </article>
                ))}
            </div>

            <div className="border border-[#00aeef]/30 mt-12 sm:mt-16 p-8 sm:p-10 lg:p-16 relative overflow-hidden">
                <div className="absolute inset-0  opacity-20" />
                <div className="relative max-w-2xl">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#0077b6] mb-3">{t("research.partnershipEyebrow")}</div>
                    <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-extrabold uppercase tracking-tighter">
                        {t("research.partnershipTitle")}
                    </h2>
                    <p className="text-neutral-600 mt-4 leading-relaxed text-sm sm:text-base">
                        {t("research.partnershipBody")}
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 mt-6 sm:mt-8 bg-[#0089c8] hover:bg-[#0077b6] rounded-lg text-white font-bold uppercase tracking-[0.18em] text-xs px-5 sm:px-7 py-3.5 sm:py-4"
                    >
                        {t("research.apply")} <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
